import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Check, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PLAN_LIMITS, getUpgradeOptions, formatCapShort } from '../lib/planLimits';

const PDF_MIN_TIER = 'Silver';
const ADVANCE_BUFFER_MS = 500;   // wait after a phase finishes before switching
const SAFETY_CAP_MS = 11000;     // hard ceiling per phase in case onDone never fires

const ORBS = [
  { left:'5%',  top:'12%', w:300, color:'rgba(30,27,75,0.14)',   dur:13, dx:52,  dy:-44 },
  { left:'74%', top:'64%', w:260, color:'rgba(245,158,11,0.09)', dur:15, dx:-50, dy:46  },
  { left:'40%', top:'38%', w:360, color:'rgba(30,27,75,0.07)',   dur:18, dx:32,  dy:-52 },
  { left:'82%', top:'10%', w:190, color:'rgba(245,158,11,0.08)', dur:11, dx:-54, dy:38  },
  { left:'18%', top:'76%', w:220, color:'rgba(76,29,149,0.08)',  dur:16, dx:38,  dy:-30 },
];
const BackgroundOrbs = () => (
  <div style={{ position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none',zIndex:0 }}>
    {ORBS.map((o,i) => (
      <motion.div key={i}
        animate={{ x:[0,o.dx,-o.dx*0.4,0], y:[0,o.dy,-o.dy*0.3,0] }}
        transition={{ duration:o.dur,repeat:Infinity,ease:'easeInOut',repeatType:'mirror' }}
        style={{ position:'absolute',left:o.left,top:o.top,width:o.w,height:o.w,
          marginLeft:-o.w/2,marginTop:-o.w/2,borderRadius:'50%',
          background:o.color,filter:'blur(70px)' }}
      />
    ))}
  </div>
);

function useTypewriter(fullText, { startDelay=0, charInterval=14, active=true }={}) {
  const [displayedText, setDisplayedText] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!active) { setDisplayedText(''); setDone(false); return; }
    setDisplayedText(''); setDone(false);
    let charIdx=0, iv;
    const startT = setTimeout(() => {
      iv = setInterval(() => {
        charIdx++;
        setDisplayedText(fullText.slice(0,charIdx));
        if (charIdx>=fullText.length) { clearInterval(iv); setDone(true); }
      }, charInterval);
    }, startDelay);
    return () => { clearTimeout(startT); if(iv) clearInterval(iv); };
  }, [fullText, startDelay, charInterval, active]);
  return { displayedText, done };
}

const BlinkingCursor = ({ color='#1E1B4B' }) => (
  <motion.span
    animate={{ opacity:[1,0,1] }}
    transition={{ duration:0.85, repeat:Infinity, ease:'steps(1)' }}
    style={{ display:'inline-block',width:1.5,height:'1em',background:color,
      marginLeft:2,verticalAlign:'text-bottom' }}
  />
);

/* ── A4 page shell ───────────────────────────────────────────────────────── */
const MockPage = ({ children, width=330 }) => (
  <div style={{
    width, aspectRatio:'0.707', flexShrink:0,
    background:'#ffffff', borderRadius:12,
    border:'3px solid #1E1B4B',
    boxShadow:'0 32px 80px rgba(0,0,0,0.50), 0 2px 0 rgba(245,158,11,0.35)',
    padding:5, overflow:'hidden',
  }}>
    <div style={{ height:'100%', border:'1.5px solid #F59E0B', borderRadius:8,
      display:'flex', flexDirection:'column', overflow:'hidden' }}>
      {children}
    </div>
  </div>
);

const PageHeaderBar = ({ label, pageNum }) => (
  <div style={{ background:'#1E1B4B', padding:'8px 13px',
    display:'flex', alignItems:'center', gap:7, flexShrink:0 }}>
    <picture>
      <source srcSet="/TechGenieMascot.webp" type="image/webp" />
      <img src="/TechGenieMascot.png" alt=""
        style={{ width:17,height:17,objectFit:'contain' }}/>
    </picture>
    <span style={{ fontSize:9, fontWeight:800, color:'white', letterSpacing:'0.4px' }}>
      TeachGenie.ai
    </span>
    {label && (
      <span style={{ marginLeft:'auto', fontSize:7.5, fontWeight:700,
        color:'rgba(255,255,255,0.9)',
        background:'rgba(245,158,11,0.28)', padding:'2px 7px', borderRadius:99 }}>
        {label}
      </span>
    )}
    {pageNum && (
      <span style={{ fontSize:7, fontWeight:600, color:'rgba(255,255,255,0.5)',
        marginLeft: label ? 5 : 'auto' }}>
        p.{pageNum}
      </span>
    )}
  </div>
);

const PageFooterBar = ({ text }) => (
  <div style={{ background:'linear-gradient(90deg,#1E1B4B,#4c1d95)', padding:'6px 13px',
    marginTop:'auto', flexShrink:0,
    display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
    <picture>
      <source srcSet="/TechGenieMascot.webp" type="image/webp" />
      <img src="/TechGenieMascot.png" alt=""
        style={{ width:10,height:10,objectFit:'contain' }}/>
    </picture>
    <span style={{ fontSize:7, color:'rgba(255,255,255,0.9)', fontWeight:700, letterSpacing:'0.2px' }}>
      Powered by TeachGenie™.ai{text ? ` · ${text}` : ''}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 1 — COVER
═══════════════════════════════════════════════════════════════════════════ */
const PhaseCover = ({ onDone = () => {} }) => {
  const [lineIn,   setLineIn]   = useState(false);
  const [metaIn,   setMetaIn]   = useState(false);
  const [badgesIn, setBadgesIn] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setLineIn(true),   550);
    const t2 = setTimeout(() => setMetaIn(true),    850);
    const t3 = setTimeout(() => setBadgesIn(true), 1150);
    const t4 = setTimeout(onDone, 2000);
    return () => [t1,t2,t3,t4].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ y:90, opacity:0, scale:0.82, rotateX:18 }}
        animate={{ y:0, opacity:1, scale:1, rotateX:0 }}
        transition={{ duration:0.75, ease:[0.22,1,0.36,1] }}
        style={{ transformOrigin:'bottom center' }}>
        <MockPage>
          <div style={{ height:5, background:'linear-gradient(90deg,#F59E0B,#fbbf24,#F59E0B)',
            flexShrink:0 }}/>
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
            justifyContent:'center', gap:13, padding:'22px 22px', textAlign:'center',
            position:'relative' }}>
            <div style={{ position:'absolute', inset:0, opacity:0.04,
              backgroundImage:'radial-gradient(#1E1B4B 1px, transparent 1px)',
              backgroundSize:'18px 18px' }}/>

            <motion.div
              initial={{ scale:0, rotate:-20 }} animate={{ scale:1, rotate:0 }}
              transition={{ type:'spring', stiffness:300, damping:14, delay:0.2 }}
              style={{ width:66, height:66, borderRadius:18,
                background:'linear-gradient(135deg,#1E1B4B,#4c1d95)',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 14px 36px rgba(30,27,75,0.45)', zIndex:1 }}>
              <picture>
                <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                <img src="/TechGenieMascot.png" alt=""
                  style={{ width:46,height:46,objectFit:'contain' }}/>
              </picture>
            </motion.div>

            <div style={{ zIndex:1 }}>
              <motion.div
                initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.32, duration:0.45 }}
                style={{ fontSize:7.5, fontWeight:700, color:'#F59E0B',
                  letterSpacing:'2.2px', textTransform:'uppercase', marginBottom:8 }}>
                AI-Powered Lesson Handout
              </motion.div>
              <motion.div
                initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.45, duration:0.45 }}
                style={{ fontSize:20, fontWeight:900, color:'#1E1B4B',
                  letterSpacing:'-0.5px', lineHeight:1.2, textTransform:'uppercase' }}>
                PHOTOSYNTHESIS
              </motion.div>
            </div>

            {lineIn && (
              <motion.div
                initial={{ width:0 }} animate={{ width:'62%' }}
                transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}
                style={{ height:2.5,
                  background:'linear-gradient(90deg,transparent,#F59E0B,transparent)',
                  borderRadius:2, zIndex:1 }}
              />
            )}

            {metaIn && (
              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
                style={{ fontSize:9, color:'#6b7280', fontWeight:600, zIndex:1 }}>
                Science · Class 8 · Chapter 4
              </motion.div>
            )}

            {badgesIn && (
              <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
                style={{ display:'flex', flexWrap:'wrap', gap:5,
                  justifyContent:'center', zIndex:1 }}>
                {['10 Pages','8 Sections','Trends & News','Practice','Scenario Quiz'].map((b,i) => (
                  <motion.span key={b}
                    initial={{ opacity:0, scale:0.7 }} animate={{ opacity:1, scale:1 }}
                    transition={{ delay:i*0.06, type:'spring', stiffness:360, damping:18 }}
                    style={{ fontSize:6.5, fontWeight:700, color:'#1E1B4B',
                      background:'rgba(30,27,75,0.07)',
                      border:'1px solid rgba(30,27,75,0.18)',
                      padding:'2.5px 7px', borderRadius:99 }}>
                    {b}
                  </motion.span>
                ))}
              </motion.div>
            )}
          </div>
          <PageFooterBar text="Complete Revision Material"/>
        </MockPage>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 2 — BUCKLE UP (dense: hook, why-it-matters, facts, stat chips)
═══════════════════════════════════════════════════════════════════════════ */
const HOOK = 'Every breath you take depends on a process you can watch happen on a windowsill.';
const WHY_LINES = [
  'Photosynthesis is the primary natural process that converts sunlight directly into',
  'stored chemical energy — every food chain on Earth traces back to it.',
];
const HOOK_FACTS = [
  'Converts light energy into glucose, releasing oxygen as a byproduct',
  'Chlorophyll absorbs red & blue light, reflecting green back to your eyes',
  'A single leaf runs millions of light-reactions every second in sunlight',
  'Without it, atmospheric oxygen would have never reached breathable levels',
];
const STAT_CHIPS = [
  { big:'90%', small:'of Earth\'s O₂ from oceans + plants', color:'#1E1B4B' },
  { big:'2.7B', small:'years this process has existed', color:'#F59E0B' },
  { big:'0%', small:'waste — every byproduct is reused', color:'#4c1d95' },
];

const PhaseBuckleUp = ({ onDone = () => {} }) => {
  const hook = useTypewriter(HOOK, { startDelay:200, charInterval:11 });
  const [whyIn, setWhyIn]   = useState(false);
  const [factsIn, setFactsIn] = useState([]);
  const [chipsIn, setChipsIn] = useState([]);

  useEffect(() => {
    if (!hook.done) return;
    const t1 = setTimeout(() => setWhyIn(true), 120);
    HOOK_FACTS.forEach((_,i) => setTimeout(() => setFactsIn(p=>[...p,i]), 480+i*180));
    const factsEnd = 480 + HOOK_FACTS.length*180;
    STAT_CHIPS.forEach((_,i) => setTimeout(() => setChipsIn(p=>[...p,i]), factsEnd+150+i*110));
    const chipsEnd = factsEnd+150+STAT_CHIPS.length*110;
    const tDone = setTimeout(onDone, chipsEnd+450);
    return () => { clearTimeout(t1); clearTimeout(tDone); };
  }, [hook.done, onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, y:30, rotateX:10 }} animate={{ opacity:1, y:0, rotateX:0 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockPage>
          <PageHeaderBar label="⚡ Buckle Up" pageNum="2"/>
          <div style={{ flex:1, padding:'11px 14px', display:'flex',
            flexDirection:'column', gap:8, overflowY:'hidden' }}>

            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:24, height:24, borderRadius:7, flexShrink:0,
                background:'linear-gradient(135deg,#F59E0B,#d97706)',
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontSize:13 }}>⚡</span>
              </div>
              <div>
                <div style={{ fontSize:11, fontWeight:900, color:'#1E1B4B', lineHeight:1 }}>
                  Buckle Up!
                </div>
                <div style={{ fontSize:6.5, color:'#9ca3af', fontWeight:500, marginTop:1.5 }}>
                  Your lesson hook
                </div>
              </div>
            </div>

            <div style={{ background:'linear-gradient(135deg,#fef9ec,#fff8e1)',
              border:'1.5px solid #F59E0B', borderRadius:8,
              padding:'7px 10px', fontSize:7.5, color:'#374151',
              lineHeight:1.5, fontStyle:'italic', fontWeight:500 }}>
              "{hook.displayedText}
              {!hook.done && <BlinkingCursor color="#d97706"/>}"
            </div>

            {whyIn && (
              <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
                transition={{ duration:0.3 }}>
                <div style={{ fontSize:7, fontWeight:800, color:'#1E1B4B',
                  letterSpacing:'1px', textTransform:'uppercase', opacity:0.45, marginBottom:3 }}>
                  Why This Matters
                </div>
                {WHY_LINES.map((l,i) => (
                  <div key={i} style={{ fontSize:7, color:'#4b5563', lineHeight:1.55, fontWeight:500 }}>
                    {l}
                  </div>
                ))}
              </motion.div>
            )}

            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              <div style={{ fontSize:7, fontWeight:800, color:'#1E1B4B',
                letterSpacing:'1px', textTransform:'uppercase', opacity:0.45 }}>
                Quick Facts
              </div>
              {HOOK_FACTS.map((f,i) => factsIn.includes(i) && (
                <motion.div key={i} initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }}
                  transition={{ duration:0.3 }}
                  style={{ display:'flex', alignItems:'flex-start', gap:6 }}>
                  <div style={{ width:12, height:12, borderRadius:'50%', flexShrink:0,
                    marginTop:1, background:['#1E1B4B','#F59E0B','#4c1d95','#0891b2'][i],
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ color:'#fff', fontSize:6.5, fontWeight:900 }}>{i+1}</span>
                  </div>
                  <span style={{ fontSize:7, color:'#374151', lineHeight:1.45 }}>{f}</span>
                </motion.div>
              ))}
            </div>

            <div style={{ display:'flex', gap:5, marginTop:'auto' }}>
              {STAT_CHIPS.map((s,i) => chipsIn.includes(i) && (
                <motion.div key={i} initial={{ opacity:0, scale:0.7, y:8 }}
                  animate={{ opacity:1, scale:1, y:0 }}
                  transition={{ type:'spring', stiffness:340, damping:18 }}
                  style={{ flex:1, background:`${s.color}0c`, border:`1px solid ${s.color}30`,
                    borderRadius:7, padding:'5px 6px', textAlign:'center' }}>
                  <div style={{ fontSize:10.5, fontWeight:900, color:s.color }}>{s.big}</div>
                  <div style={{ fontSize:5.5, color:'#6b7280', fontWeight:600, lineHeight:1.3, marginTop:1 }}>
                    {s.small}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <PageFooterBar text="Page 2 of 10"/>
        </MockPage>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 3 — OBJECTIVES + KEY TAKEAWAYS
═══════════════════════════════════════════════════════════════════════════ */
const OBJECTIVES = [
  { t:'Define what photosynthesis is',     rbt:'Remember',   c:'#1e40af' },
  { t:"Describe chlorophyll's role",       rbt:'Understand', c:'#4338ca' },
  { t:'Explain the two reaction stages',   rbt:'Understand', c:'#4338ca' },
  { t:'Apply the rate equation',           rbt:'Apply',       c:'#6d28d9' },
  { t:'Compare C3, C4 and CAM',            rbt:'Analyze',     c:'#be185d' },
  { t:'Evaluate the best climate fit',     rbt:'Evaluate',    c:'#c2410c' },
  { t:'Design a rate experiment',          rbt:'Create',      c:'#b91c1c' },
];
const TAKEAWAYS = [
  '6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂',
  'Chloroplasts are the site of all photosynthetic reactions',
  'Stomata regulate CO₂ intake and O₂ / water vapor release',
  'Light intensity, CO₂ level and temperature all limit reaction rate',
  'Excess glucose is converted to starch for long-term storage',
  'Light reactions occur in the thylakoid',
  'Carotenoids capture extra light wavelengths chlorophyll misses',
  'Photosynthesis and respiration form a gas-exchange cycle',
];

const PhaseObjectives = ({ onDone = () => {} }) => {
  const [objsIn, setObjsIn] = useState([]);
  const [takIn,  setTakIn]  = useState([]);
  useEffect(() => {
    OBJECTIVES.forEach((_,i) => setTimeout(() => setObjsIn(p=>[...p,i]), 180+i*190));
    TAKEAWAYS.forEach((_,i)  => setTimeout(() => setTakIn(p=>[...p,i]),  350+i*170));
    const objsEnd = 180+OBJECTIVES.length*190;
    const takEnd  = 350+TAKEAWAYS.length*170;
    const t = setTimeout(onDone, Math.max(objsEnd,takEnd)+450);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, x:40, rotateY:-20 }} animate={{ opacity:1, x:0, rotateY:0 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockPage>
          <PageHeaderBar label="Objectives & Takeaways" pageNum="3"/>
          <div style={{ flex:1, padding:'11px 12px', display:'flex', gap:10, overflow:'hidden' }}>

            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:13 }}>
              <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:1 }}>
                <div style={{ width:4, height:13, background:'#1E1B4B', borderRadius:2 }}/>
                <span style={{ fontSize:7, fontWeight:900, color:'#1E1B4B',
                  textTransform:'uppercase', letterSpacing:'0.6px' }}>Objectives</span>
              </div>
              {OBJECTIVES.map((obj,i) => objsIn.includes(i) && (
                <motion.div key={i} initial={{ opacity:0, x:-14 }} animate={{ opacity:1, x:0 }}
                  transition={{ duration:0.32, ease:[0.22,1,0.36,1] }}
                  style={{ display:'flex', alignItems:'flex-start', gap:6 }}>
                  <div style={{ width:13, height:13, borderRadius:4, flexShrink:0,
                    background:'#1E1B4B', marginTop:1,
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Check size={7} color="#F59E0B" strokeWidth={3.5}/>
                  </div>
                  <div style={{ flex:1, fontSize:0, lineHeight:0 }}>
                    <span style={{ fontSize:7.3, color:'#374151', lineHeight:1.4,
                      fontWeight:500 }}>{obj.t}</span>
                    <div style={{ marginTop:4, fontSize:0, lineHeight:0 }}>
                      <span style={{ display:'inline-block', fontSize:6.2, lineHeight:1.2,
                        fontWeight:700, color:obj.c, background:`${obj.c}14`,
                        padding:'1.5px 6px', borderRadius:99 }}>{obj.rbt}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div style={{ width:1, background:'#f0f0f0', flexShrink:0 }}/>

            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:11 }}>
              <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:1 }}>
                <div style={{ width:4, height:13, background:'#F59E0B', borderRadius:2 }}/>
                <span style={{ fontSize:7, fontWeight:900, color:'#1E1B4B',
                  textTransform:'uppercase', letterSpacing:'0.6px' }}>Key Takeaways</span>
              </div>
              {TAKEAWAYS.map((tak,i) => takIn.includes(i) && (
                <motion.div key={i} initial={{ opacity:0, x:14 }} animate={{ opacity:1, x:0 }}
                  transition={{ duration:0.32, ease:[0.22,1,0.36,1] }}
                  style={{ display:'flex', alignItems:'flex-start', gap:6 }}>
                  <span style={{ width:5, height:5, borderRadius:'50%', background:'#F59E0B',
                    flexShrink:0, marginTop:5, display:'block',
                    boxShadow:'0 0 4px rgba(245,158,11,0.55)' }}/>
                  <span style={{ fontSize:7.3, color:'#374151', lineHeight:1.5,
                    fontWeight:500 }}>{tak}</span>
                </motion.div>
              ))}
            </div>
          </div>
          <PageFooterBar text="Page 3 of 10"/>
        </MockPage>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 4 — CONTENT SECTIONS (TOC, denser)
═══════════════════════════════════════════════════════════════════════════ */
const CONTENT_SECS = [
  { num:1, title:'Introduction to Photosynthesis', desc:'Sets up the definition, location, and real-world relevance before diving into mechanism.', bullets:['Definition','Where it happens'], color:'#1E1B4B' },
  { num:2, title:'The Chloroplast',                 desc:'Structure of the organelle — thylakoid stacks, stroma, and pigment placement.',                  bullets:['Thylakoid','Stroma'],          color:'#4c1d95' },
  { num:3, title:'Light Reactions',                 desc:'How Photosystem I & II convert light into ATP and NADPH, releasing O₂.',                          bullets:['ATP & NADPH','O₂ release'],    color:'#d97706' },
  { num:4, title:'Calvin Cycle (Dark Reaction)',     desc:'CO₂ fixation via RuBisCO, leading to G3P and eventually glucose formation.',                       bullets:['CO₂ fixation','RuBisCO'],      color:'#0891b2' },
  { num:5, title:'Factors Affecting Rate',           desc:'Light intensity, CO₂ concentration and temperature — and how each limits the rate.',               bullets:['Light & CO₂','Temperature'],   color:'#059669' },
];

const PhaseContent = ({ onDone = () => {} }) => {
  const [secIn, setSecIn] = useState([]);
  const [tagIn, setTagIn] = useState(false);
  useEffect(() => {
    CONTENT_SECS.forEach((_,i) => setTimeout(() => setSecIn(p=>[...p,i]), 130+i*260));
    const secsEnd = 130+CONTENT_SECS.length*260;
    const t1 = setTimeout(() => setTagIn(true), secsEnd+120);
    const t2 = setTimeout(onDone, secsEnd+650);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, y:-30, scale:0.94 }} animate={{ opacity:1, y:0, scale:1 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockPage>
          <PageHeaderBar label="📖 Lesson Content" pageNum="4"/>
          <div style={{ flex:1, padding:'9px 12px', display:'flex',
            flexDirection:'column', gap:5, overflowY:'hidden' }}>
            <div style={{ fontSize:6.5, fontWeight:800, color:'#9ca3af',
              letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:1 }}>
              5 Sections · Curriculum-Aligned
            </div>
            {CONTENT_SECS.map((sec,i) => secIn.includes(i) && (
              <motion.div key={i}
                initial={{ opacity:0, y:14, x:-8 }} animate={{ opacity:1, y:0, x:0 }}
                transition={{ duration:0.34, ease:[0.22,1,0.36,1] }}
                style={{ display:'flex', gap:7, alignItems:'flex-start',
                  padding:'5px 8px', borderRadius:6,
                  background:`${sec.color}07`,
                  border:`1.5px solid ${sec.color}25` }}>
                <div style={{ width:16, height:16, borderRadius:4, flexShrink:0,
                  background:sec.color, marginTop:0.5,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ color:'#fff', fontSize:7.5, fontWeight:900 }}>{sec.num}</span>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:7.5, fontWeight:800, color:'#1E1B4B',
                    lineHeight:1.2, marginBottom:2 }}>
                    {sec.title}
                  </div>
                  <div style={{ fontSize:6.3, color:'#6b7280', lineHeight:1.4,
                    fontWeight:500, marginBottom:3 }}>
                    {sec.desc}
                  </div>
                  <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                    {sec.bullets.map((b,j) => (
                      <span key={j} style={{ fontSize:5.8, color:sec.color,
                        fontWeight:700, background:`${sec.color}12`,
                        padding:'1px 5px', borderRadius:99 }}>
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
            {tagIn && (
              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
                style={{ fontSize:6, color:'#9ca3af', fontWeight:600,
                  textAlign:'center', marginTop:2, fontStyle:'italic' }}>
                + Summary & Exam Notes section follows
              </motion.div>
            )}
          </div>
          <PageFooterBar text="Page 4 of 10"/>
        </MockPage>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 5 — TRENDS & NEWS
═══════════════════════════════════════════════════════════════════════════ */
const TREND_ITEM = {
  title:'Vertical Farming Boom Reaches Indian Cities',
  summary:'Urban farms now use tuned LED spectrums to maximize chlorophyll absorption, growing leafy greens 3x faster than open-field farming in a fraction of the space.',
};
const NEWS_ITEM = {
  title:'Scientists Engineer Algae for Faster Carbon Capture',
  summary:'Researchers modified algal chloroplasts to fix CO₂ more efficiently, opening a new path for carbon capture facilities modeled directly on photosynthesis.',
};

const PhaseTrendsNews = ({ onDone = () => {} }) => {
  const [c1In, setC1In] = useState(false);
  const [c2In, setC2In] = useState(false);
  const [noteIn, setNoteIn] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setC1In(true),  280);
    const t2 = setTimeout(() => setC2In(true),  900);
    const t3 = setTimeout(() => setNoteIn(true),1550);
    const t4 = setTimeout(onDone, 2150);
    return () => [t1,t2,t3,t4].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, scale:0.9, y:24 }} animate={{ opacity:1, scale:1, y:0 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockPage>
          <PageHeaderBar label="📰 Trends & News" pageNum="5"/>
          <div style={{ flex:1, padding:'11px 13px', display:'flex',
            flexDirection:'column', gap:9, overflowY:'hidden' }}>
            <div style={{ fontSize:6.5, fontWeight:800, color:'#9ca3af',
              letterSpacing:'0.8px', textTransform:'uppercase' }}>
              Real-World Connections — Updated for This Lesson
            </div>

            {c1In && (
              <motion.div initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }}
                transition={{ duration:0.4, ease:[0.22,1,0.36,1] }}
                style={{ background:'#e0f2fe', border:'1.5px solid #7dd3fc',
                  borderRadius:8, padding:'8px 10px' }}>
                <span style={{ fontSize:6, fontWeight:800, color:'#075985',
                  background:'#bae6fd', padding:'1.5px 6px', borderRadius:99,
                  textTransform:'uppercase', letterSpacing:'0.4px' }}>Trend</span>
                <div style={{ fontSize:8, fontWeight:800, color:'#0c4a6e',
                  marginTop:5, lineHeight:1.3 }}>{TREND_ITEM.title}</div>
                <div style={{ fontSize:6.6, color:'#0369a1', lineHeight:1.5,
                  marginTop:3, fontWeight:500 }}>{TREND_ITEM.summary}</div>
              </motion.div>
            )}

            {c2In && (
              <motion.div initial={{ opacity:0, x:16 }} animate={{ opacity:1, x:0 }}
                transition={{ duration:0.4, ease:[0.22,1,0.36,1] }}
                style={{ background:'#fff7ed', border:'1.5px solid #fdba74',
                  borderRadius:8, padding:'8px 10px' }}>
                <span style={{ fontSize:6, fontWeight:800, color:'#9a3412',
                  background:'#fed7aa', padding:'1.5px 6px', borderRadius:99,
                  textTransform:'uppercase', letterSpacing:'0.4px' }}>News</span>
                <div style={{ fontSize:8, fontWeight:800, color:'#7c2d12',
                  marginTop:5, lineHeight:1.3 }}>{NEWS_ITEM.title}</div>
                <div style={{ fontSize:6.6, color:'#9a3412', lineHeight:1.5,
                  marginTop:3, fontWeight:500 }}>{NEWS_ITEM.summary}</div>
              </motion.div>
            )}

            {noteIn && (
              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
                style={{ fontSize:6.3, color:'#9ca3af', fontWeight:600,
                  fontStyle:'italic', textAlign:'center', marginTop:'auto' }}>
                Auto-refreshed each time you generate — always current
              </motion.div>
            )}
          </div>
          <PageFooterBar text="Page 5 of 10"/>
        </MockPage>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 6 — PRACTICE EXERCISES (PYQ style)
═══════════════════════════════════════════════════════════════════════════ */
const EX1 = {
  q:'If a plant is kept under red light only, which stage of photosynthesis is most directly affected first?',
  options:{ A:'Calvin Cycle', B:'Light Reactions', C:'Transpiration', D:'Root absorption' },
};
const EX2 = {
  q:'Explain why the rate of photosynthesis declines sharply at temperatures above 45°C despite strong light.',
};

const PhaseExercises = ({ onDone = () => {} }) => {
  const [c1In, setC1In] = useState(false);
  const [optsIn, setOptsIn] = useState([]);
  const [revealIn, setRevealIn] = useState(false);
  const [c2In, setC2In] = useState(false);
  const [linesIn, setLinesIn] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setC1In(true), 220);
    const optKeys = Object.keys(EX1.options);
    optKeys.forEach((_,i) => setTimeout(() => setOptsIn(p=>[...p,i]), 600+i*120));
    const optsEnd = 600+optKeys.length*120;
    const t2 = setTimeout(() => setRevealIn(true), optsEnd+150);
    const t3 = setTimeout(() => setC2In(true), optsEnd+650);
    const t4 = setTimeout(() => setLinesIn(true), optsEnd+1050);
    const t5 = setTimeout(onDone, optsEnd+1700);
    return () => [t1,t2,t3,t4,t5].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, y:26, scale:0.95 }} animate={{ opacity:1, y:0, scale:1 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockPage>
          <PageHeaderBar label="🏆 Practice (PYQ)" pageNum="8"/>
          <div style={{ flex:1, padding:'10px 12px', display:'flex',
            flexDirection:'column', gap:8, overflowY:'hidden' }}>

            {c1In && (
              <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                transition={{ duration:0.35 }}
                style={{ borderRadius:8, border:'1px solid #e5e7eb', overflow:'hidden',
                  boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ background:'linear-gradient(135deg,#1e3a8a,#1e40af)',
                  padding:'5px 9px', display:'flex', alignItems:'center', gap:5, flexWrap:'wrap' }}>
                  <span style={{ background:'#fff', color:'#1e3a8a', padding:'1px 6px',
                    borderRadius:99, fontSize:6, fontWeight:800 }}>Q1</span>
                  <span style={{ background:'#f59e0b', color:'#fff', padding:'1px 6px',
                    borderRadius:99, fontSize:5.8, fontWeight:700, textTransform:'uppercase' }}>
                    Intermediate
                  </span>
                  <span style={{ background:'rgba(255,255,255,0.18)', color:'#fff',
                    padding:'1px 6px', borderRadius:99, fontSize:5.8, fontWeight:600 }}>MCQ</span>
                  <span style={{ background:'#f59e0b', color:'#fff', padding:'1px 6px',
                    borderRadius:99, fontSize:5.8, fontWeight:700 }}>CBSE '23</span>
                  <span style={{ background:'#ddd6fe', color:'#6d28d9', padding:'1px 6px',
                    borderRadius:99, fontSize:5.8, fontWeight:700, marginLeft:'auto' }}>Apply</span>
                </div>
                <div style={{ padding:'7px 10px' }}>
                  <div style={{ fontSize:7, color:'#111827', lineHeight:1.5,
                    fontWeight:600, marginBottom:6 }}>{EX1.q}</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:3, marginBottom:6 }}>
                    {Object.entries(EX1.options).map(([k,v],i) => optsIn.includes(i) && (
                      <motion.div key={k} initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }}
                        transition={{ duration:0.25 }}
                        style={{ display:'flex', gap:6, alignItems:'center',
                          background:'#f9fafb', borderRadius:5, padding:'4px 7px',
                          border:'1px solid #e5e7eb' }}>
                        <span style={{ background:'#1e3a8a', color:'#fff', width:13, height:13,
                          borderRadius:'50%', display:'flex', alignItems:'center',
                          justifyContent:'center', fontSize:6, fontWeight:700, flexShrink:0 }}>{k}</span>
                        <span style={{ fontSize:6.5, color:'#374151' }}>{v}</span>
                      </motion.div>
                    ))}
                  </div>
                  {revealIn && (
                    <motion.div initial={{ opacity:0, scale:0.85 }} animate={{ opacity:1, scale:1 }}
                      style={{ display:'inline-flex', alignItems:'center', gap:4,
                        background:'linear-gradient(135deg,#10b981,#059669)', color:'#fff',
                        borderRadius:99, padding:'3px 9px', fontSize:6, fontWeight:700 }}>
                      ✓ Reveal Answer
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}

            {c2In && (
              <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                transition={{ duration:0.35 }}
                style={{ borderRadius:8, border:'1px solid #e5e7eb', overflow:'hidden',
                  boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ background:'linear-gradient(135deg,#1e3a8a,#1e40af)',
                  padding:'5px 9px', display:'flex', alignItems:'center', gap:5, flexWrap:'wrap' }}>
                  <span style={{ background:'#fff', color:'#1e3a8a', padding:'1px 6px',
                    borderRadius:99, fontSize:6, fontWeight:800 }}>Q2</span>
                  <span style={{ background:'#ef4444', color:'#fff', padding:'1px 6px',
                    borderRadius:99, fontSize:5.8, fontWeight:700, textTransform:'uppercase' }}>
                    Hard
                  </span>
                  <span style={{ background:'rgba(255,255,255,0.18)', color:'#fff',
                    padding:'1px 6px', borderRadius:99, fontSize:5.8, fontWeight:600 }}>
                    Short Answer
                  </span>
                  <span style={{ background:'#fce7f3', color:'#be185d', padding:'1px 6px',
                    borderRadius:99, fontSize:5.8, fontWeight:700, marginLeft:'auto' }}>Analyze</span>
                </div>
                <div style={{ padding:'7px 10px' }}>
                  <div style={{ fontSize:7, color:'#111827', lineHeight:1.5,
                    fontWeight:600, marginBottom:6 }}>{EX2.q}</div>
                  {linesIn && (
                    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}>
                      <div style={{ height:1, background:'#e5e7eb', marginBottom:5 }}/>
                      <div style={{ height:1, background:'#e5e7eb', width:'78%' }}/>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
          <PageFooterBar text="Page 8 of 10"/>
        </MockPage>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 7 — SCENARIO QUIZ (scan-line reveal)
═══════════════════════════════════════════════════════════════════════════ */
const SCENARIO_TEXT = 'Riya keeps one basil plant on a sunny windowsill and an identical one inside a dark cupboard. Both are watered equally for two weeks.';
const SCENARIO_Q = 'Based on the scenario, which factor most directly explains why the windowsill plant grew taller and greener?';
const SCENARIO_OPTS = {
  A:'Difference in water absorbed by roots',
  B:'Difference in light available for photosynthesis',
  C:'Difference in soil temperature only',
  D:'Difference in pot size',
};

const PhaseScenarioQuiz = ({ onDone = () => {} }) => {
  const [scanLine, setScanLine] = useState(false);
  const [scenarioIn, setScenarioIn] = useState(false);
  const [qIn, setQIn] = useState(false);
  const [optsIn, setOptsIn] = useState([]);
  const [noteIn, setNoteIn] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setScanLine(true), 200);
    const t2 = setTimeout(() => setScenarioIn(true), 300);
    const t3 = setTimeout(() => setQIn(true), 850);
    const optKeys = Object.keys(SCENARIO_OPTS);
    optKeys.forEach((_,i) => setTimeout(() => setOptsIn(p=>[...p,i]), 1150+i*130));
    const optsEnd = 1150+optKeys.length*130;
    const t4 = setTimeout(() => setNoteIn(true), optsEnd+150);
    const t5 = setTimeout(onDone, optsEnd+650);
    return () => [t1,t2,t3,t4,t5].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, rotateY:25, x:-30 }} animate={{ opacity:1, rotateY:0, x:0 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockPage>
          <PageHeaderBar label="🎯 Scenario Quiz" pageNum="9"/>
          <div style={{ flex:1, padding:'11px 13px', display:'flex',
            flexDirection:'column', gap:7, position:'relative', overflow:'hidden' }}>

            {scanLine && (
              <motion.div
                initial={{ top:0 }} animate={{ top:'100%' }}
                transition={{ duration:2, ease:'linear' }}
                style={{ position:'absolute', left:0, right:0, height:2,
                  background:'linear-gradient(90deg,transparent,#F59E0B,transparent)',
                  boxShadow:'0 0 14px rgba(245,158,11,0.85)', zIndex:10,
                  pointerEvents:'none' }}
              />
            )}

            {scenarioIn && (
              <motion.div initial={{ opacity:0, scale:0.96 }} animate={{ opacity:1, scale:1 }}
                transition={{ duration:0.35 }}
                style={{ background:'#f5f3ff', border:'1.5px solid #c4b5fd',
                  borderRadius:8, padding:'7px 9px' }}>
                <span style={{ fontSize:6, fontWeight:800, color:'#6d28d9',
                  textTransform:'uppercase', letterSpacing:'0.5px' }}>Scenario</span>
                <div style={{ fontSize:6.8, color:'#4c1d95', lineHeight:1.5,
                  marginTop:3, fontStyle:'italic', fontWeight:500 }}>
                  {SCENARIO_TEXT}
                </div>
              </motion.div>
            )}

            {qIn && (
              <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
                transition={{ duration:0.32 }}
                style={{ fontSize:7.2, color:'#111827', lineHeight:1.5, fontWeight:700 }}>
                {SCENARIO_Q}
              </motion.div>
            )}

            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {Object.entries(SCENARIO_OPTS).map(([k,v],i) => optsIn.includes(i) && (
                <motion.div key={k} initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }}
                  transition={{ duration:0.28 }}
                  style={{ display:'flex', gap:6, alignItems:'center',
                    background: i===1 ? '#ecfdf5' : '#f9fafb',
                    border: i===1 ? '1.5px solid #6ee7b7' : '1px solid #e5e7eb',
                    borderRadius:6, padding:'4px 8px' }}>
                  <span style={{ background: i===1 ? '#059669' : '#1e3a8a', color:'#fff',
                    width:13, height:13, borderRadius:'50%', display:'flex',
                    alignItems:'center', justifyContent:'center', fontSize:6,
                    fontWeight:700, flexShrink:0 }}>{k}</span>
                  <span style={{ fontSize:6.6, color:'#374151', fontWeight:500 }}>{v}</span>
                  {i===1 && <Check size={9} color="#059669" strokeWidth={3} style={{ marginLeft:'auto' }}/>}
                </motion.div>
              ))}
            </div>

            {noteIn && (
              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
                style={{ fontSize:6, color:'#9ca3af', fontWeight:600,
                  fontStyle:'italic', marginTop:'auto', textAlign:'center' }}>
                ✓ Full step-by-step reasoning included in the explanation
              </motion.div>
            )}
          </div>
          <PageFooterBar text="Page 9 of 10"/>
        </MockPage>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 8 — HANDOUT FINALE (big, colorful payoff)
═══════════════════════════════════════════════════════════════════════════ */
const StudentSilhouette = ({ color='#94a3b8', size=36 }) => (
  <svg viewBox="0 0 40 48" width={size} height={size*1.2}>
    <circle cx="20" cy="12" r="10" fill={color}/>
    <path d="M2 46 C2 30 8 24 20 24 C32 24 38 30 38 46 Z" fill={color}/>
  </svg>
);

const MiniContentPage = ({ hColor, lineColors }) => (
  <div style={{ position:'absolute', inset:0, borderRadius:9, overflow:'hidden',
    background:'#fff', display:'flex', flexDirection:'column' }}>
    <div style={{ height:20, background:hColor,
      display:'flex', alignItems:'center', paddingLeft:8, gap:5, flexShrink:0 }}>
      <div style={{ width:9,height:9,borderRadius:'50%',background:'rgba(255,255,255,0.5)' }}/>
      <div style={{ width:58,height:4,background:'rgba(255,255,255,0.6)',borderRadius:2 }}/>
    </div>
    <div style={{ height:2, background:'#F59E0B', opacity:0.65, flexShrink:0 }}/>
    <div style={{ padding:'8px 10px', display:'flex', flexDirection:'column', gap:5 }}>
      {lineColors.map((c,i) => (
        <div key={i} style={{ height:4.5, borderRadius:2, background:c,
          width:`${90-i*8}%`, opacity:0.5+i*0.05 }}/>
      ))}
      <div style={{ display:'flex', gap:4, marginTop:3 }}>
        <div style={{ width:5,height:5,borderRadius:'50%',background:'#F59E0B',marginTop:0.5 }}/>
        <div style={{ height:4.5, borderRadius:2, background:'rgba(245,158,11,0.4)', width:'62%' }}/>
      </div>
      <div style={{ display:'flex', gap:4 }}>
        <div style={{ width:5,height:5,borderRadius:'50%',background:'#F59E0B',marginTop:0.5 }}/>
        <div style={{ height:4.5, borderRadius:2, background:'rgba(245,158,11,0.4)', width:'50%' }}/>
      </div>
    </div>
    <div style={{ marginTop:'auto', height:13,
      background:'linear-gradient(90deg,#1E1B4B,#4c1d95)',
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:74,height:2.5,background:'rgba(255,255,255,0.4)',borderRadius:2 }}/>
    </div>
  </div>
);

const PAGE_STACK = [
  { rotate:-13, x:-30, y:10, hColor:'#1E1B4B', lines:['rgba(30,27,75,0.25)','rgba(30,27,75,0.18)','rgba(30,27,75,0.12)','rgba(30,27,75,0.08)'] },
  { rotate:-5,  x:-12, y:-4, hColor:'#d97706', lines:['rgba(217,119,6,0.30)','rgba(217,119,6,0.20)','rgba(30,27,75,0.12)','rgba(30,27,75,0.08)'] },
  { rotate:0,   x:0,   y:-11,hColor:'#1E1B4B', lines:['rgba(30,27,75,0.28)','rgba(30,27,75,0.20)','rgba(245,158,11,0.25)','rgba(30,27,75,0.10)'] },
  { rotate:5,   x:12,  y:-4, hColor:'#0891b2', lines:['rgba(8,145,178,0.28)','rgba(8,145,178,0.18)','rgba(30,27,75,0.12)','rgba(30,27,75,0.08)'] },
  { rotate:13,  x:30,  y:10, hColor:'#7c3aed', lines:['rgba(124,58,237,0.28)','rgba(124,58,237,0.18)','rgba(30,27,75,0.12)','rgba(30,27,75,0.08)'] },
];
const SILHOUETTES = [
  { color:'#94a3b8', delay:0    },
  { color:'#a8a29e', delay:0.12 },
  { color:'#b0c4de', delay:0.22 },
];
const PAYOFF_CHIPS = [
  { label:'10 Pages',      color:'#1E1B4B' },
  { label:'Print-Ready',   color:'#F59E0B' },
  { label:'Works Offline', color:'#7c3aed' },
];

const PhaseHandbook = ({ onDone = () => {} }) => {
  const [stackIn,       setStackIn]       = useState(false);
  const [collapsing,    setCollapsing]    = useState(false);
  const [bookletIn,     setBookletIn]     = useState(false);
  const [bookletMoved,  setBookletMoved]  = useState(false);
  const [silhouettesIn, setSilhouettesIn] = useState(false);
  const [chipsIn,       setChipsIn]       = useState([]);
  const [captionIn,     setCaptionIn]     = useState(false);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStackIn(true),        100),
      setTimeout(() => setCollapsing(true),    1400),
      setTimeout(() => setBookletIn(true),      2100),
      setTimeout(() => setBookletMoved(true),   3000),
      setTimeout(() => setSilhouettesIn(true),  3500),
      setTimeout(() => setChipsIn([0]),         3900),
      setTimeout(() => setChipsIn(p=>[...p,1]), 4050),
      setTimeout(() => setChipsIn(p=>[...p,2]), 4200),
      setTimeout(() => setCaptionIn(true),      4500),
      setTimeout(onDone,                        5200),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',flexDirection:'column',
      alignItems:'center',justifyContent:'center',padding:'10px 14px',gap:14,
      position:'relative' }}>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
        gap: bookletMoved ? 40 : 0,
        transition:'gap 0.65s cubic-bezier(0.22,1,0.36,1)', minHeight:330 }}>

        {/* Stack / booklet */}
        <div style={{ position:'relative', width:225, height:316 }}>
          {/* Pulsing colored glow behind the booklet */}
          {bookletIn && (
            <motion.div
              animate={{ opacity:[0.35,0.65,0.35], scale:[1,1.08,1] }}
              transition={{ duration:2.2, repeat:Infinity, ease:'easeInOut' }}
              style={{ position:'absolute', inset:-26, borderRadius:30, zIndex:-1,
                background:'radial-gradient(circle, rgba(245,158,11,0.30), rgba(124,58,237,0.18), transparent 70%)',
                filter:'blur(18px)' }}
            />
          )}

          <AnimatePresence>
            {!bookletIn && stackIn && PAGE_STACK.map((p,i) => (
              <motion.div key={i}
                initial={{ opacity:0, scale:0.5, rotate:0, x:0, y:30 }}
                animate={{
                  opacity:1, scale:1,
                  rotate: collapsing ? 0 : p.rotate,
                  x:      collapsing ? 0 : p.x,
                  y:      collapsing ? 0 : p.y,
                }}
                exit={{ opacity:0, scale:0.88, transition:{ duration:0.26 } }}
                transition={{
                  delay: collapsing ? 0 : 0.07+i*0.08,
                  duration:0.5, ease:[0.22,1,0.36,1],
                }}
                style={{ position:'absolute', inset:0,
                  border:'2px solid rgba(30,27,75,0.45)', borderRadius:9,
                  boxShadow:'0 14px 38px rgba(0,0,0,0.30)',
                  transformOrigin:'center bottom' }}>
                <MiniContentPage hColor={p.hColor} lineColors={p.lines}/>
              </motion.div>
            ))}
          </AnimatePresence>

          <AnimatePresence>
            {bookletIn && (
              <motion.div
                initial={{ opacity:0, scale:0.7, y:20 }}
                animate={{ opacity:1, scale: bookletMoved ? 0.86 : 1, y:0 }}
                transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}
                style={{ position:'absolute', inset:0, background:'#ffffff',
                  borderRadius:9, border:'3px solid #1E1B4B', overflow:'hidden',
                  boxShadow:'0 26px 64px rgba(0,0,0,0.40), 0 0 0 1px rgba(245,158,11,0.25)' }}>
                <div style={{ position:'absolute', left:0, top:0, bottom:0, width:14,
                  background:'linear-gradient(90deg,rgba(30,27,75,0.28),transparent)' }}/>
                <div style={{ height:28, background:'#1E1B4B',
                  display:'flex', alignItems:'center', paddingLeft:20, paddingRight:10, gap:6 }}>
                  <picture>
                    <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                    <img src="/TechGenieMascot.png" alt=""
                      style={{ width:15,height:15,objectFit:'contain' }}/>
                  </picture>
                  <span style={{ fontSize:8, fontWeight:800, color:'white' }}>
                    TeachGenie.ai
                  </span>
                </div>
                <div style={{ height:2, background:'#F59E0B', opacity:0.7 }}/>
                <div style={{ padding:'10px 16px 10px 20px', display:'flex', flexDirection:'column', gap:0 }}>
                  {/* Topic + subject line */}
                  <div style={{ fontSize:6, fontWeight:700, color:'#9ca3af', letterSpacing:'1.2px',
                    textTransform:'uppercase', marginBottom:3 }}>Science · Class 8 · Chapter 4</div>
                  <div style={{ fontSize:12.5, fontWeight:900, color:'#1E1B4B', letterSpacing:'-0.3px',
                    lineHeight:1.15, marginBottom:7 }}>PHOTOSYNTHESIS</div>

                  {/* Divider */}
                  <div style={{ height:1.5, background:'linear-gradient(90deg,#F59E0B,transparent)',
                    borderRadius:2, marginBottom:7 }}/>

                  {/* Objectives mini-section */}
                  <div style={{ fontSize:5.8, fontWeight:800, color:'#1E1B4B', letterSpacing:'0.9px',
                    textTransform:'uppercase', opacity:0.5, marginBottom:5 }}>Learning Objectives</div>
                  {[
                    'Define the process and its role in ecosystems',
                    'Identify the two stages: light & dark reactions',
                    'Apply the rate equation to real scenarios',
                    'Compare C3, C4 and CAM plant pathways',
                  ].map((txt, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:5, marginBottom:4 }}>
                      <div style={{ width:5, height:5, borderRadius:'50%', flexShrink:0, marginTop:1.5,
                        background:['#1E1B4B','#F59E0B','#7c3aed','#0891b2'][i] }}/>
                      <span style={{ fontSize:6.3, color:'#374151', lineHeight:1.4, fontWeight:500 }}>{txt}</span>
                    </div>
                  ))}

                  {/* Key equation box */}
                  <div style={{ marginTop:5, background:'#fef9ec', border:'1px solid #F59E0B',
                    borderRadius:5, padding:'4px 7px' }}>
                    <div style={{ fontSize:5.5, fontWeight:800, color:'#d97706', letterSpacing:'0.8px',
                      textTransform:'uppercase', marginBottom:2 }}>Key Equation</div>
                    <div style={{ fontSize:6.8, fontWeight:700, color:'#1E1B4B', letterSpacing:'0.2px' }}>
                      6CO₂ + 6H₂O + light → C₆H₁₂O₆ + 6O₂
                    </div>
                  </div>
                </div>
                <div style={{ position:'absolute', top:0, right:22, width:16, height:46,
                  background:'#F59E0B',
                  clipPath:'polygon(0 0,100% 0,100% 100%,50% 82%,0 100%)' }}/>
                <div style={{ position:'absolute', bottom:0, left:0, right:0, height:15,
                  background:'linear-gradient(90deg,#1E1B4B,#4c1d95)',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <div style={{ width:68,height:2.5,background:'rgba(255,255,255,0.4)',borderRadius:2 }}/>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {silhouettesIn && (
            <motion.div
              initial={{ opacity:0, x:34 }} animate={{ opacity:1, x:0 }}
              transition={{ duration:0.46, ease:[0.22,1,0.36,1] }}
              style={{ display:'flex', alignItems:'flex-end', gap:15 }}>
              {SILHOUETTES.map((s,i) => (
                <motion.div key={i}
                  initial={{ opacity:0, y:18, scale:0.72 }}
                  animate={{ opacity:1, y:0, scale:1 }}
                  transition={{ delay:s.delay, type:'spring', stiffness:300, damping:18 }}>
                  <StudentSilhouette color={s.color} size={48+i*6}/>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div style={{ display:'flex', gap:7, justifyContent:'center', minHeight:20 }}>
        {PAYOFF_CHIPS.map((c,i) => chipsIn.includes(i) && (
          <motion.span key={c.label}
            initial={{ opacity:0, y:8, scale:0.8 }} animate={{ opacity:1, y:0, scale:1 }}
            transition={{ type:'spring', stiffness:340, damping:18 }}
            style={{ fontSize:9, fontWeight:800, color:'#fff',
              background:c.color, padding:'4px 11px', borderRadius:99,
              boxShadow:`0 4px 14px ${c.color}55` }}>
            ✓ {c.label}
          </motion.span>
        ))}
      </div>

      <AnimatePresence>
        {captionIn && (
          <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
            transition={{ duration:0.4 }}
            style={{ textAlign:'center' }}>
            <div style={{ fontSize:16, fontWeight:800,
              color:'rgba(255,255,255,0.92)', letterSpacing:'-0.2px' }}>
              One handout. <span style={{ color:'#F59E0B' }}>Every</span> student.
            </div>
            <div style={{ fontSize:11.5, color:'rgba(255,255,255,0.42)',
              marginTop:5, fontStyle:'italic' }}>
              Print-ready. No login required.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Phases ──────────────────────────────────────────────────────────────── */
const PHASES = [
  { id:'cover',     label:'Cover',    Component:PhaseCover        },
  { id:'buckleup',  label:'Hook',     Component:PhaseBuckleUp     },
  { id:'objs',      label:'Goals',    Component:PhaseObjectives   },
  { id:'content',   label:'Content',  Component:PhaseContent      },
  { id:'trends',    label:'Trends',   Component:PhaseTrendsNews   },
  { id:'exercises', label:'Practice', Component:PhaseExercises    },
  { id:'scenario',  label:'Scenario', Component:PhaseScenarioQuiz },
  { id:'handbook',  label:'Handout',  Component:PhaseHandbook     },
];

/* ── Left panel data ─────────────────────────────────────────────────────── */
const PDF_FEATS = [
  'Complete lesson in one print-ready PDF — cover, content, quiz prep & more',
  'Every section included: Buckle Up, Objectives, Trends, Practice & Scenario Quiz',
  'Hand to your students as-is — no formatting or editing needed',
];
/* ═══════════════════════════════════════════════════════════════════════════
   MAIN MODAL
═══════════════════════════════════════════════════════════════════════════ */
const PDFUpgradeModal = ({ isOpen, onClose, currentTier = 'free' }) => {
  const navigate = useNavigate();
  const [phase,    setPhase]    = useState(0);
  const [barReady, setBarReady] = useState(false); // true once current phase's content finished
  const [mobile, setMobile] = useState(() => window.innerWidth < 700);

  // Plans worth offering depend on what the user already has — never suggest a downgrade.
  const planOptions = useMemo(() => getUpgradeOptions(currentTier), [currentTier]);
  const hasAnyPdfAccess = (PLAN_LIMITS[(currentTier || 'free').toLowerCase()]?.pdf ?? 0) > 0;
  const onlyInstitutional = planOptions.length === 1 && planOptions[0].key === 'institutional';

  const [selPlan, setSelPlan] = useState(planOptions[0]?.key || 'silver');
  useEffect(() => { setSelPlan(planOptions[0]?.key || 'silver'); }, [planOptions]);

  const advanceRef = useRef(null);
  const safetyRef  = useRef(null);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 700);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const goNext = useCallback(() => {
    setPhase(p => (p+1) % PHASES.length);
  }, []);

  const handlePhaseDone = useCallback(() => {
    setBarReady(true);
    clearTimeout(advanceRef.current);
    advanceRef.current = setTimeout(goNext, ADVANCE_BUFFER_MS);
  }, [goNext]);

  useEffect(() => {
    if (!isOpen) return;
    setBarReady(false);
    clearTimeout(safetyRef.current);
    safetyRef.current = setTimeout(goNext, SAFETY_CAP_MS);
    return () => {
      clearTimeout(advanceRef.current);
      clearTimeout(safetyRef.current);
    };
  }, [isOpen, phase, goNext]);

  useEffect(() => { if (isOpen) setPhase(0); }, [isOpen]);

  const handleNavClick = (i) => {
    clearTimeout(advanceRef.current);
    clearTimeout(safetyRef.current);
    setPhase(i);
  };

  const { Component: PhaseComp } = PHASES[phase];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={onClose}
            style={{ position:'fixed', inset:0, background:'rgba(4,2,18,0.78)',
              backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', zIndex:9998 }}
          />

          <motion.div
            style={{ position:'fixed', inset:0, display:'flex', alignItems:'center',
              justifyContent:'center', pointerEvents:'none', zIndex:9999, padding:'16px' }}
            initial={{ opacity:0, scale:0.87, y:52 }}
            animate={{ opacity:1, scale:1, y:0 }}
            exit={{ opacity:0, scale:0.87, y:52 }}
            transition={{ duration:0.5, ease:[0.34,1.56,0.64,1] }}>

            <div style={{ pointerEvents:'auto', maxWidth:960, width:'100%',
              maxHeight:'calc(100vh - 32px)', borderRadius:26, overflow:'hidden',
              boxShadow:'0 80px 200px -20px rgba(0,0,0,0.88), 0 0 0 1px rgba(255,255,255,0.06)',
              display:'flex', flexDirection:mobile?'column':'row', position:'relative' }}>

              <button onClick={onClose}
                style={{ position:'absolute', top:14, right:14,
                  background:'rgba(0,0,0,0.55)', backdropFilter:'blur(10px)',
                  border:'1px solid rgba(255,255,255,0.14)', color:'rgba(255,255,255,0.7)',
                  width:30, height:30, borderRadius:'50%',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  cursor:'pointer', zIndex:30, transition:'all 0.18s' }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.16)'; e.currentTarget.style.color='#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background='rgba(0,0,0,0.55)'; e.currentTarget.style.color='rgba(255,255,255,0.7)'; }}>
                <X size={12}/>
              </button>

              {/* ── LEFT PANEL ───────────────────────────────────────────── */}
              <div style={{ width:mobile?'100%':284, flexShrink:0, background:'#fff',
                padding:'28px 22px 26px', display:'flex', flexDirection:'column',
                overflowY:'auto', order:mobile?2:1 }}>

                <motion.div
                  initial={{ scale:0, rotate:-18 }} animate={{ scale:1, rotate:0 }}
                  transition={{ type:'spring', stiffness:220, delay:0.1 }}
                  style={{ width:52, height:52, borderRadius:14, marginBottom:16,
                    background:'linear-gradient(135deg,#1E1B4B,#4c1d95)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow:'0 8px 24px rgba(30,27,75,0.3)' }}>
                  <FileText size={22} color="#F59E0B"/>
                </motion.div>

                <motion.h2 initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
                  transition={{ delay:0.15 }}
                  style={{ fontSize:18, fontWeight:900, color:'#111827', marginBottom:6,
                    lineHeight:1.26, letterSpacing:'-0.3px' }}>
                  {hasAnyPdfAccess ? "You've Used Your PDF Limit" : 'Unlock PDF Export'}
                </motion.h2>

                <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }}
                  transition={{ delay:0.22 }}
                  style={{ fontSize:11.5, color:'#6b7280', lineHeight:1.6,
                    marginBottom:18, fontWeight:400 }}>
                  {hasAnyPdfAccess
                    ? <>You've exported PDFs for every lesson your plan allows. Upgrade for a higher lesson cap{onlyInstitutional ? ', or go Institutional for unlimited PDF exports.' : '.'}</>
                    : <>Export polished, multi-page PDF handouts your students can read offline — available from <strong>{PDF_MIN_TIER}</strong> onward.</>}
                </motion.p>

                <div style={{ display:'flex', flexDirection:'column', gap:9, marginBottom:18 }}>
                  {PDF_FEATS.map((f,i) => (
                    <motion.div key={f} initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }}
                      transition={{ delay:0.26+i*0.07 }}
                      style={{ display:'flex', alignItems:'flex-start', gap:9 }}>
                      <div style={{ width:16, height:16, borderRadius:'50%', flexShrink:0,
                        marginTop:1.5,
                        background:'linear-gradient(135deg,#1E1B4B,#4c1d95)',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Check size={8} color="#fff" strokeWidth={3}/>
                      </div>
                      <span style={{ fontSize:11.5, color:'#6b7280', lineHeight:1.5,
                        fontWeight:400 }}>{f}</span>
                    </motion.div>
                  ))}
                </div>

                <div style={{ height:1, background:'#f0f0f0', marginBottom:14 }}/>
                <p style={{ fontSize:9, fontWeight:700, color:'#9ca3af',
                  textTransform:'uppercase', letterSpacing:'1.4px', marginBottom:9 }}>
                  Select Plan
                </p>

                <div style={{ display:'flex', flexDirection:'column', gap:7, marginBottom:20 }}>
                  {planOptions.map((plan, idx) => {
                    const sel = selPlan === plan.key;
                    const isFirstPdfTier = !hasAnyPdfAccess && idx === 0;
                    const isRecommended = hasAnyPdfAccess && idx === 0 && !onlyInstitutional;
                    return (
                      <motion.div key={plan.key}
                        onClick={() => setSelPlan(plan.key)}
                        whileHover={{ y:-1 }} whileTap={{ scale:0.99 }}
                        style={{ padding:'8px 11px', borderRadius:10, cursor:'pointer',
                          border:`1.5px solid ${sel ? plan.dot : '#e5e7eb'}`,
                          background: sel ? `${plan.dot}08` : '#fafafa',
                          transition:'border 0.14s,background 0.14s',
                          position:'relative' }}>
                        {plan.popular && (
                          <span style={{ position:'absolute', top:-7, right:10, color:'#fff',
                            fontSize:7, fontWeight:700, padding:'1px 7px', borderRadius:99,
                            textTransform:'uppercase', background:'#f59e0b' }}>
                            Popular
                          </span>
                        )}
                        {isFirstPdfTier && !plan.popular && (
                          <span style={{ position:'absolute', top:-7, right:10, color:'#fff',
                            fontSize:7, fontWeight:700, padding:'1px 7px', borderRadius:99,
                            textTransform:'uppercase', background:'#1E1B4B' }}>
                            Unlocks PDF
                          </span>
                        )}
                        {isRecommended && !plan.popular && (
                          <span style={{ position:'absolute', top:-7, right:10, color:'#fff',
                            fontSize:7, fontWeight:700, padding:'1px 7px', borderRadius:99,
                            textTransform:'uppercase', background:'#1E1B4B' }}>
                            Recommended
                          </span>
                        )}
                        <div style={{ display:'flex', justifyContent:'space-between',
                          alignItems:'center', marginBottom:4 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <div style={{ width:7, height:7, borderRadius:'50%',
                              background:plan.dot,
                              boxShadow:sel?`0 0 6px ${plan.dot}`:'none' }}/>
                            <span style={{ fontSize:12.5, fontWeight:700, color:'#111827' }}>
                              {plan.name}
                            </span>
                          </div>
                          <div style={{ display:'flex', alignItems:'baseline', gap:3 }}>
                            <span style={{ fontSize:13, fontWeight:800, color:'#111827' }}>
                              {plan.price}
                            </span>
                            {plan.original && (
                              <s style={{ fontSize:9, color:'#d1d5db', fontWeight:500 }}>
                                {plan.original}
                              </s>
                            )}
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:6 }}>
                          <span style={{ fontSize:8, fontWeight:600, color:plan.dot,
                            background:`${plan.dot}14`, borderRadius:99, padding:'1px 6px' }}>
                            PDF: {formatCapShort(plan.pdf)}
                          </span>
                          <span style={{ fontSize:8, fontWeight:600, color:plan.dot,
                            background:`${plan.dot}14`, borderRadius:99, padding:'1px 6px' }}>
                            PPT: {formatCapShort(plan.ppt)}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <motion.button
                  onClick={() => {
                    onClose();
                    if (selPlan === 'institutional') navigate('/contact');
                    else navigate(`/pricing?plan=${selPlan}`);
                  }}
                  whileHover={{ y:-2, boxShadow:'0 16px 36px rgba(30,27,75,0.42)' }}
                  whileTap={{ scale:0.98 }}
                  style={{ width:'100%', height:46, borderRadius:50, border:'none',
                    background:'linear-gradient(135deg,#1E1B4B,#4c1d95)',
                    color:'#F59E0B', fontSize:13.5, fontWeight:800, cursor:'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:7,
                    boxShadow:'0 6px 20px rgba(30,27,75,0.24)', marginBottom:9,
                    letterSpacing:'0.2px' }}>
                  <Zap size={13} fill="#F59E0B" strokeWidth={0}/>
                  {selPlan === 'institutional' ? 'Contact Sales' : hasAnyPdfAccess ? 'Upgrade for More PDF Exports' : 'Unlock PDF Export Now'}
                </motion.button>

                <button onClick={onClose}
                  style={{ width:'100%', height:36, borderRadius:50, background:'transparent',
                    border:'none', color:'#9ca3af', fontSize:12, fontWeight:500,
                    cursor:'pointer', transition:'color 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.color='#6b7280'; }}
                  onMouseLeave={e => { e.currentTarget.style.color='#9ca3af'; }}>
                  Maybe Later
                </button>
              </div>

              {/* ── RIGHT PANEL ──────────────────────────────────────────── */}
              <div style={{ flex:1, background:'#060916',
                display:'flex', flexDirection:'column',
                minHeight:mobile?560:600,
                borderRadius:mobile?'24px 24px 0 0':'0 26px 26px 0',
                overflow:'hidden', position:'relative', order:mobile?1:2 }}>

                <BackgroundOrbs/>

                {/* Progress bar — fills while content plays, snaps to 100% when done */}
                <motion.div key={`pb-${phase}`}
                  initial={{ width:'0%' }}
                  animate={{ width: barReady ? '100%' : '88%' }}
                  transition={{ duration: barReady ? 0.3 : 5.5,
                    ease: barReady ? 'easeOut' : 'linear' }}
                  style={{ height:2.5, flexShrink:0, zIndex:10, position:'relative',
                    background:'linear-gradient(90deg,#1E1B4B,#F59E0B)',
                    boxShadow:'0 0 10px rgba(245,158,11,0.65)' }}
                />

                <div style={{ flex:1, position:'relative', overflow:'hidden',
                  zIndex:1, perspective:1200 }}>
                  <AnimatePresence mode="wait">
                    <motion.div key={phase}
                      initial={{ opacity:0, rotateY:-42, x:55, scale:0.94 }}
                      animate={{ opacity:1, rotateY:0, x:0, scale:1 }}
                      exit={{ opacity:0, rotateY:42, x:-55, scale:0.94,
                        transition:{ duration:0.28 } }}
                      transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}
                      style={{ position:'absolute', inset:0, transformStyle:'preserve-3d' }}>
                      <PhaseComp onDone={handlePhaseDone}/>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Phase nav */}
                <div style={{ height:54, flexShrink:0, position:'relative', zIndex:10,
                  background:'rgba(6,9,22,0.97)',
                  borderTop:'0.5px solid rgba(255,255,255,0.07)',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {PHASES.map((step,i) => (
                    <React.Fragment key={step.id}>
                      <button onClick={() => handleNavClick(i)}
                        style={{ background:'none', border:'none', cursor:'pointer',
                          outline:'none', display:'flex', flexDirection:'column',
                          alignItems:'center', gap:3, padding:'0 3px' }}>
                        <div style={{ position:'relative', width:16, height:16,
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {i === phase && (
                            <motion.div
                              animate={{ scale:[1,2.4,1], opacity:[0.35,0,0.35] }}
                              transition={{ duration:1.6, repeat:Infinity, ease:'easeOut' }}
                              style={{ position:'absolute', width:16, height:16,
                                borderRadius:'50%', border:'1px solid #F59E0B',
                                pointerEvents:'none' }}
                            />
                          )}
                          <motion.div
                            animate={{
                              width:      i===phase ? 9 : 5.5,
                              height:     i===phase ? 9 : 5.5,
                              background: i===phase ? '#F59E0B'
                                : i<phase ? 'rgba(245,158,11,0.45)'
                                : 'rgba(255,255,255,0.15)',
                              boxShadow:  i===phase ? '0 0 8px rgba(245,158,11,0.65)' : 'none',
                            }}
                            transition={{ duration:0.2 }}
                            style={{ borderRadius:'50%' }}
                          />
                        </div>
                        <span style={{ fontSize:7, whiteSpace:'nowrap',
                          color:i===phase ? 'rgba(255,255,255,0.86)' : 'rgba(255,255,255,0.24)',
                          fontWeight:i===phase ? 700 : 400, transition:'color 0.22s' }}>
                          {step.label}
                        </span>
                      </button>
                      {i < PHASES.length-1 && (
                        <motion.div
                          animate={{ background: i<phase
                            ? 'rgba(245,158,11,0.45)'
                            : 'rgba(255,255,255,0.07)' }}
                          transition={{ duration:0.4 }}
                          style={{ width:10, height:1, flexShrink:0, marginBottom:12 }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PDFUpgradeModal;
